/**
 * Unlock effects — the read side of meta progression (docs/05 §4, docs/12 §3).
 *
 * `progression.ts` is the write side: it banks runs, awards tracks and flips
 * unlock ids on. Until this file existed nothing ever *asked* whether an unlock
 * was held, so 66 unlocks accumulated into a save and changed nothing. This is
 * the layer that turns "unlocked" into "in the pool".
 *
 * Three rules shape everything here:
 *
 *   1. **Pure query.** Nothing in this file mutates a `Progression` except
 *      `tryPurchase`, which delegates to `Progression.purchase`. Everything else
 *      is a projection of state the caller already owns, so call sites can run
 *      it per-frame, in a UI render, or in a test without side effects.
 *
 *   2. **Gate by class/family, expand to ids.** docs/05 §4 says Salvage unlocks
 *      "weapon classes, module families" — never individual weapons. So the
 *      unlock table talks about classes, and this file is the only place that
 *      knows how a class becomes a list of concrete weapon ids.
 *
 *   3. **Degrade, never throw.** Meta and content drift apart constantly: a
 *      weapon class gets renamed in combat/, a new one ships before anyone adds
 *      a Salvage entry for it. A mismatch must cost the player *access*, not the
 *      run. Anything this file cannot resolve resolves to "available" — content
 *      leaking into the pool early is a balance bug; content sealed behind a
 *      typo is unreachable forever.
 *
 * Decoupled by design, matching its neighbours: catalogues arrive as structural
 * `{ id, class }` / `{ id, family }` shapes, so `WeaponDef[]` and `ModuleDef[]`
 * pass straight in without this module importing anything from combat/. That
 * also keeps it free of `weaponData`'s material-registration ordering trap.
 */

import { ARCHETYPE_BY_ID, ARCHETYPE_IDS, DEFAULT_ARCHETYPE, TRAVERSAL_ORDER } from './archetypes';
import type { TraversalAbilityId } from './archetypes';
import { DEFAULT_KIT, KIT_IDS } from './kits';
import { UNLOCKS, UNLOCK_BY_ID } from './progression';
import type { Progression, UnlockDef } from './progression';

// ------------------------------------------------------------- catalogues

/** Structural view of a weapon. `WeaponDef` satisfies this. */
export interface WeaponLike {
  readonly id: string;
  readonly class: string;
}

/** Structural view of a module. `ModuleDef` satisfies this. */
export interface ModuleLike {
  readonly id: string;
  readonly family: string;
}

// ------------------------------------------------------------ id bridging

/**
 * The unlock table and combat/ do not agree on class spelling, and neither is
 * wrong: progression says `shard-caster` because it is prose the player reads,
 * combat says `shardCaster` because it is a TypeScript union member. Canonical
 * form (lowercase, alphanumerics only) reconciles most of them; the rest are
 * genuine word differences and are listed explicitly rather than derived by
 * suffix-stripping, because a table you can read is worth more than a rule you
 * have to simulate.
 */
const CLASS_ALIASES: Readonly<Record<string, string>> = {
  chainweapon: 'chain',
  railweapon: 'rail',
  beamemitter: 'beam',
  livingweapon: 'living',
  droneweapon: 'drone',
  bioweapon: 'bio',
  alchemicalsprayer: 'sprayer',
  // `thrown-rig`/`thrownRig`, `relic-blade`/`relicBlade`, `shard-caster`/
  // `shardCaster` and the rest already agree once canonicalised.
};

function canonical(token: string): string {
  return token.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Progression's spelling or combat's spelling in, one comparable key out. */
function classKey(token: string): string {
  const c = canonical(token);
  return CLASS_ALIASES[c] ?? c;
}

function familyKey(token: string): string {
  return canonical(token);
}

// ------------------------------------------------------------- gated sets

function collectGrantTokens(
  kind: 'weapons' | 'modules',
  key: (token: string) => string,
): ReadonlySet<string> {
  const out = new Set<string>();
  for (const def of UNLOCKS) {
    const g = def.grants;
    if (g.kind === 'weapons' && kind === 'weapons') for (const c of g.classes) out.add(key(c));
    if (g.kind === 'modules' && kind === 'modules') for (const f of g.families) out.add(key(f));
  }
  return out;
}

/**
 * Every weapon class any unlock gates. Its complement — computed against the
 * catalogue the caller passes, not a hardcoded list — is the baseline.
 *
 * Today that complement is sword, dagger, gauntlet and pistol: the four classes
 * the Salvage table deliberately never mentions, and exactly the contents of the
 * Wanderer's and Duelist's kits. Deriving it rather than restating it means a
 * 30th weapon class added to combat/ tomorrow is playable tomorrow instead of
 * silently unreachable until someone notices (rule 3).
 */
export const GATED_WEAPON_CLASSES: ReadonlySet<string> = collectGrantTokens('weapons', classKey);

/** Every module family any unlock gates. Currently all twelve of them. */
export const GATED_MODULE_FAMILIES: ReadonlySet<string> = collectGrantTokens('modules', familyKey);

/**
 * The one module family that is never gated.
 *
 * Unlike weapons, the Salvage table covers *all twelve* module families, so the
 * derived baseline would be empty — and an empty module pool does not gate the
 * slot system, it switches it off: run one would drop no modules at all and
 * every weapon would carry five dead sockets. Kinetic is the elementless family
 * (impact, recoil, counterweight — pure physics, no reaction chemistry), which
 * makes it the module equivalent of the starting sword: the thing you have
 * before you have learned anything. `salvage.modules.industrial` still delivers
 * galvanic and corrosive, so nothing on the ramp becomes worthless.
 *
 * Empty this constant to return to a strictly derived baseline.
 */
export const BASELINE_MODULE_FAMILIES: readonly string[] = ['kinetic'];

const BASELINE_MODULE_KEYS: ReadonlySet<string> = new Set(BASELINE_MODULE_FAMILIES.map(familyKey));

// ---------------------------------------------------------------- weapons

/**
 * Weapon ids that may enter this profile's pool: baseline classes plus every
 * class a Salvage unlock has handed over. Order follows the catalogue, so a
 * caller's authored order (rarity, class grouping) survives the filter.
 */
export function unlockedWeaponIds(
  prog: Progression,
  allWeapons: readonly WeaponLike[],
): string[] {
  const owned = new Set(prog.unlockedWeaponClasses().map(classKey));
  return allWeapons.filter((w) => isWeaponClassAvailable(w.class, owned)).map((w) => w.id);
}

// `baselineWeaponIds` lived here and was deleted by #118's sweep, along with
// `baselineModuleIds` below. Both said "exposed so tests and UI can state it",
// and after however many months neither a test nor a panel had. They were
// `unlockedWeaponIds` with the owned set replaced by the empty one — one line
// each, reconstructible in less time than judging them a second time took.
// Deliberately *not* rescued by writing the test they name: a dead export you
// can revive by writing a caller for it is one you can revive anywhere, and a
// gate that accepts that is a gate you can always talk your way past.

function isWeaponClassAvailable(cls: string, owned: ReadonlySet<string>): boolean {
  const key = classKey(cls);
  // Ungated class -> baseline. This is rule 3's load-bearing line: a class the
  // Salvage table forgot is playable, not lost.
  if (!GATED_WEAPON_CLASSES.has(key)) return true;
  return owned.has(key);
}

// ---------------------------------------------------------------- modules

export function unlockedModuleIds(
  prog: Progression,
  allModules: readonly ModuleLike[],
): string[] {
  const owned = new Set(prog.unlockedModuleFamilies().map(familyKey));
  return allModules.filter((m) => isModuleFamilyAvailable(m.family, owned)).map((m) => m.id);
}

function isModuleFamilyAvailable(family: string, owned: ReadonlySet<string>): boolean {
  const key = familyKey(family);
  if (BASELINE_MODULE_KEYS.has(key)) return true;
  if (!GATED_MODULE_FAMILIES.has(key)) return true;
  return owned.has(key);
}

// ------------------------------------------------------------- archetypes

/**
 * Bodies selectable at run start. Lineage is the feed: dying to a fungal beast
 * advances the Parasite Colony channel until its threshold trips (docs/05 §4).
 *
 * Duelist is force-included. `createRunState` *throws* on an un-unlocked
 * archetype, so a save that lost its default unlock — content drift, a hand-
 * edited profile, a normalise pass that dropped a renamed id — would otherwise
 * hard-fail every run start with no way back. The default body is the floor.
 * Ids with no matching `ArchetypeDef` are dropped for the same reason in
 * reverse: offering a body `getArchetype` cannot resolve throws downstream.
 */
export function unlockedArchetypeIds(prog: Progression): string[] {
  const owned = new Set(prog.unlockedArchetypes());
  owned.add(DEFAULT_ARCHETYPE);
  // ARCHETYPE_IDS order, so the selection screen never reshuffles between runs.
  return ARCHETYPE_IDS.filter((id) => owned.has(id));
}

// ------------------------------------------------------------------- kits

/**
 * Kits selectable at run start, in authored order so the screen never
 * reshuffles between runs.
 *
 * The Wanderer's Kit is force-included for the same reason Duelist is in
 * `unlockedArchetypeIds`: it is the floor. It is `defaultUnlocked` and costs 0
 * Ash, so the only way to be without it is drift — and a run-setup screen with
 * no selectable kit offers a choice of nothing. Ids with no `KitDef` are dropped
 * in the same breath, because offering a kit `getKit` cannot resolve would put
 * an empty hand into the run.
 */
export function unlockedKitIds(prog: Progression): string[] {
  const owned = new Set(prog.unlockedKits());
  owned.add(DEFAULT_KIT);
  return KIT_IDS.filter((id) => owned.has(id));
}

// ------------------------------------------------------------- attunement

export interface AttunementView {
  /** How many abilities may be carried into a run. Starts at 0, by design. */
  slots: number;
  /** Ability ids that may be picked *right now*. Empty while slots are 0. */
  available: string[];
}

/**
 * The Attunement track's whole surface.
 *
 * Slots start at zero and that is the point (docs/05 §4, docs/02 §3.1): run one
 * is a pure Metroidvania where every one of the nine abilities is found in the
 * world. Each slot converts one previously *earned* ability into a starting
 * condition, which is how a late run becomes a build-expression sprint instead.
 *
 * `available` is empty at zero slots rather than listing earned-but-unusable
 * abilities, mirroring `Progression.runStartOptions()`. A caller that renders
 * `available` as a picker therefore cannot offer a choice that would be
 * rejected. UI wanting to show "earned, no slot yet" should read
 * `Progression.unlockables()`, which models the whole tree.
 */
export function attunedAbilities(prog: Progression): AttunementView {
  const slots = prog.attunementSlots();
  return { slots, available: slots > 0 ? [...prog.attunedAbilities()] : [] };
}

/**
 * What a run actually begins with: the archetype's innate abilities, plus up to
 * `slots` valid picks, merged without duplicates.
 *
 * Invalid picks are dropped rather than raised. This is a projection — the place
 * that *reports* a bad choice is `Progression.setStartAbilities`, which returns
 * a reason. By the time a run is being created the answer has to be a usable
 * list, so a stale pick (a slot lost, an ability id renamed) costs the player
 * that pick and nothing else.
 *
 * A pick the body already has innately is skipped without consuming its slot:
 * paying a scarce slot for something Breaker gets free is a trap, not a choice.
 */
export function startingAbilitiesFor(
  prog: Progression,
  archetypeId: string,
  chosen: readonly string[],
): string[] {
  // Unknown archetype -> no innates rather than a throw; the caller is about to
  // validate the id anyway, and a crash here would mask the real error.
  const innate: readonly TraversalAbilityId[] = ARCHETYPE_BY_ID[archetypeId]?.innateAbilities ?? [];
  const innateSet = new Set<string>(innate);

  const { slots, available } = attunedAbilities(prog);
  const allowed = new Set(available);

  const picks: string[] = [];
  for (const id of chosen) {
    if (picks.length >= slots) break;
    if (!allowed.has(id)) continue; // unattuned, unknown, or zero slots
    if (innateSet.has(id)) continue; // already free on this body
    if (picks.includes(id)) continue; // a duplicate must not burn two slots
    picks.push(id);
  }

  const held = new Set<string>([...innateSet, ...picks]);
  // Critical-path order (docs/02 §3.1) so HUD chips and save files are stable.
  const ordered: string[] = TRAVERSAL_ORDER.filter((a) => held.has(a));
  // Anything an archetype grants that TRAVERSAL_ORDER has not heard of yet still
  // ships — dropping a body's innate ability is worse than an odd sort position.
  for (const id of held) if (!ordered.includes(id)) ordered.push(id);
  return ordered;
}

// `storedStartingAbilitiesFor` lived here and was deleted by #118's sweep. It
// was `startingAbilitiesFor(prog, id, prog.state.startAbilities)` — a
// convenience for a run creation that does not take that route. There are two
// callers of the underlying function's job and neither wanted this one:
// `runSetupAdapter.resolveStartAbilities` passes the picks the player is making
// *right now*, which are not yet on the profile, and `createRunState` goes
// through `Progression.effectiveStartAbilities()`, which filters the stored
// picks against attunement rather than against an archetype. A convenience for
// a third caller that never arrived is a third spelling of run start.

// ---------------------------------------------------------------- the shop

export function ashBalance(prog: Progression): number {
  return prog.balance('ash');
}

export interface Purchasable {
  id: string;
  label: string;
  cost: number;
  /** Cost is payable from the current Ash balance. */
  affordable: boolean;
  description: string;
}

/**
 * The Ash shop: unowned `spend`-gated unlocks whose prerequisites are already
 * met, cheapest first.
 *
 * Prereq-blocked entries are excluded rather than listed as unaffordable, so
 * `affordable` means exactly one thing — "you have the Ash" — and never doubles
 * as "you are allowed". Several Ash items are gated behind *other tracks*
 * (`ash.rest.altar` needs `attune.slots.1`), and showing those as merely too
 * expensive would misdescribe them: no amount of Ash buys them. A shop that
 * wants to preview the whole ramp should read `Progression.unlockables()`.
 */
export function purchasables(prog: Progression): Purchasable[] {
  const bal = ashBalance(prog);
  return UNLOCKS.filter((d) => d.gate === 'spend' && !prog.isUnlocked(d.id) && prereqsMet(prog, d))
    .sort((a, b) => a.cost - b.cost || a.id.localeCompare(b.id))
    .map((d) => ({
      id: d.id,
      label: d.name,
      cost: d.cost,
      affordable: bal >= d.cost,
      description: d.desc,
    }));
}

export interface PurchaseResult {
  ok: boolean;
  reason?: string;
}

/**
 * `Progression.purchase` answers only true/false, and a shop button needs to say
 * *why* it refused. Re-checking the same conditions here duplicates a little
 * logic in exchange for a message; the purchase itself still goes through
 * `Progression`, so this can never debit Ash the real gate would have refused.
 */
export function tryPurchase(prog: Progression, id: string): PurchaseResult {
  const def = UNLOCK_BY_ID[id];
  if (!def) return { ok: false, reason: `no such unlock: ${id}` };
  if (def.gate !== 'spend') {
    return { ok: false, reason: `${def.name} is earned, not bought — feed the ${def.track} track` };
  }
  if (prog.isUnlocked(id)) return { ok: false, reason: `${def.name} is already unlocked` };

  const missing = def.requires.filter((r) => !prog.isUnlocked(r));
  if (missing.length > 0) return { ok: false, reason: `requires ${missing.join(', ')}` };

  const bal = ashBalance(prog);
  if (bal < def.cost) return { ok: false, reason: `costs ${def.cost} ash, you have ${bal}` };

  // Any refusal past here is a rule this file does not know about, so report it
  // as such rather than inventing a reason.
  if (!prog.purchase(id)) return { ok: false, reason: `purchase of ${def.name} refused` };
  return { ok: true };
}

function prereqsMet(prog: Progression, def: UnlockDef): boolean {
  return def.requires.every((r) => prog.isUnlocked(r));
}
